import type { I18n } from '@lingui/core';
import { Badge } from '@/components/ui/badge';
import { formatDateDisplay } from '@/lib/format';
import {
  formatCorrectionDelta,
  formatCorrectionMinutes,
  formatCorrectionSpan,
  getTimeCorrectionModeLabel,
  parseTimeCorrectionBody,
  type TimeCorrectionBody,
  type TimeCorrectionSpan,
} from '@/types/changeRequest';
import { GenericPayload } from './GenericPayload';

interface TimeCorrectionDiffProps {
  /** The `time_correction` executor body; re-checked, a body that fails renders raw. */
  body: TimeCorrectionBody | Record<string, unknown> | undefined;
  i18n: I18n;
}

function SpanCell({ span, i18n }: { span: TimeCorrectionSpan; i18n: I18n }) {
  return (
    <span className="text-sm">
      {formatCorrectionSpan(span)}
      <span className="block text-xs text-muted-foreground">
        {i18n._({
          id: 'changeRequest.timeCorrection.netBreak',
          message: '{net} net, break {break}',
          values: { net: formatCorrectionMinutes(span.netMinutes), break: formatCorrectionMinutes(span.breakMinutes) },
        })}
      </span>
    </span>
  );
}

/**
 * TimeCorrectionDiff renders a `time_correction` request (#1979): the day, the
 * rule it was filed under, what the day counted at submit, the requested span
 * and the difference, and the employee's reason. The approval writes the
 * requested span as an APPROVED time entry of source CORRECTION. State is
 * carried by text, not colour alone.
 */
export function TimeCorrectionDiff({ body, i18n }: TimeCorrectionDiffProps) {
  const parsed = parseTimeCorrectionBody(body);
  if (!parsed) {
    return <GenericPayload payload={body} i18n={i18n} data-testid="time-correction-raw" />;
  }
  const rows: { key: string; label: string; content: React.ReactNode }[] = [
    {
      key: 'old',
      label: i18n._({ id: 'changeRequest.timeCorrection.old', message: 'Counted at submit' }),
      content: <SpanCell span={parsed.old} i18n={i18n} />,
    },
    {
      key: 'new',
      label: i18n._({ id: 'changeRequest.timeCorrection.new', message: 'Requested' }),
      content: <SpanCell span={parsed.new} i18n={i18n} />,
    },
    {
      key: 'delta',
      label: i18n._({ id: 'changeRequest.timeCorrection.delta', message: 'Difference' }),
      content: <span className="text-sm font-medium">{formatCorrectionDelta(parsed.deltaMinutes)}</span>,
    },
    {
      key: 'reason',
      label: i18n._({ id: 'changeRequest.timeCorrection.reason', message: 'Reason' }),
      content: <span className="text-sm break-words">{parsed.reason}</span>,
    },
  ];

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3" data-testid="time-correction-diff">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{parsed.employeeName ?? ''}</span>
        <span className="text-sm">{formatDateDisplay(parsed.date)}</span>
        <Badge variant="secondary">{getTimeCorrectionModeLabel(i18n, parsed.mode)}</Badge>
      </div>
      <ul className="space-y-2">
        {rows.map((row) => (
          <li
            key={row.key}
            data-testid={`time-correction-${row.key}`}
            className="grid grid-cols-1 gap-1 sm:grid-cols-3 sm:gap-2"
          >
            <span className="text-sm font-medium text-muted-foreground">{row.label}</span>
            <span className="sm:col-span-2">{row.content}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
